import React from 'react';
import styles from './IncidentNumberView.module.scss';
import {
  SFChip,
  SFDivider,
  SFMenuOption,
  SFScrollable,
  SFText,
  SFTextField
} from 'sfui';
import FooterButtons, {
  FooterButtonAction
} from '../FooterButtons/FooterButtons';
import { UserContext } from 'ui-smartforce-settings';
import { isIncidentNumberValid } from '../../../../Helpers';
import { INCIDENT_NUMBER_HELP_MSG } from '../../../../Constants/Messages';
import { StateConfigContext } from '../../../../Context/StateConfigContext';
import { Field, ReportConfig, ReportValue } from '../../../../Models';
import { CaseNumberField } from './CaseNumberField/CaseNumberField';
import { IntegrationReportsContext } from '../../../../Context/IntegrationReportsContext';
import PendingReportsService from '../../../../Services/PendingReportsService';

function getCaseNumberConfig(stateConfig: ReportConfig): Field | undefined {
  const section = stateConfig.general_information.sections.find(
    (s) => s.name === 'to_connect_reports'
  );

  if (section) {
    return section.fields.find((f) => f.name === 'event_identifier');
  }
}

export interface IncidentNumberViewProps {
  onCancel: () => void;
  onContinue: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
  onCleanUp: (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => void;
}

const getIncidentNumberGenerated = (officerId: string): string => {
  const timestamp: string = new Date().getTime().toString();

  //Remove possible special characters from officer id
  const officerIdWithoutSpecial: string = officerId.replace(
    /[^A-Za-z0-9-]/g,
    ''
  );

  return `${timestamp.substring(0, 10)}-${officerIdWithoutSpecial}`;
};

const IncidentNumberView = ({
  onCancel,
  onContinue,
  onCleanUp
}: IncidentNumberViewProps): React.ReactElement<IncidentNumberViewProps> => {
  const { user } = React.useContext(UserContext);
  const stateConfig: ReportConfig = React.useContext(StateConfigContext)
    .stateConfig as ReportConfig;
  const { integrationReports } = React.useContext(IntegrationReportsContext);

  const [pendingReports, setPendingReports] = React.useState<ReportValue[]>([]);

  const incidentNumberRef = React.useRef<string>(
    getIncidentNumberGenerated(user?.officer_id as string)
  );
  const [incidentNumber, setIncidentNumber] = React.useState(
    incidentNumberRef.current
  );

  const [incidentNumberDisabled, setIncidentNumberDisabled] =
    React.useState<boolean>(false);

  const [selectedReport, setSelectedReport] = React.useState<ReportValue>();

  const [caseNumber, setCaseNumber] = React.useState<string>('');

  const continueButton: FooterButtonAction = {
    label: 'Continue',
    disabled:
      !isIncidentNumberValid(incidentNumber) ||
      (caseNumber.length > 0 && !isIncidentNumberValid(caseNumber)),
    onClick: () => onContinue(incidentNumber, caseNumber, selectedReport)
  };

  const caseNumberConfig = getCaseNumberConfig(stateConfig);

  const caseNumberOptions = (): SFMenuOption[] => {
    const availableIntegrationReports = integrationReports.filter((report) => {
      return (
        pendingReports.filter(
          (pendingReport) =>
            pendingReport.general_information.state_related?.to_connect_reports
              .event_identifier ===
            report.general_information.state_related?.to_connect_reports
              .event_identifier
        ).length === 0
      );
    });

    return availableIntegrationReports.map((report) => ({
      label: report.general_information.state_related.to_connect_reports
        .event_identifier as string,
      value: report.general_information.incident_number as string
    }));
  };

  const onCaseNumberChange = (value: string | SFMenuOption): void => {
    if (typeof value === 'string') {
      setSelectedReport(undefined);
      setIncidentNumber(incidentNumberRef.current);
      setIncidentNumberDisabled(false);
      setCaseNumber(value);
    } else {
      const reportSelected = integrationReports.filter(
        (report) => report.general_information.incident_number === value.value
      )[0];
      setSelectedReport(reportSelected);
      setIncidentNumber(
        reportSelected.general_information.incident_number as string
      );
      setIncidentNumberDisabled(true);
      setCaseNumber(value.label as string);
    }
  };

  React.useEffect(() => {
    const getPendingReports = async () => {
      const pendingReports = await PendingReportsService.getUserReports(
        user?.email as string
      );
      setPendingReports(pendingReports);
    };

    getPendingReports();
  }, [user?.email]);

  React.useEffect(() => {
    return () => {
      onCleanUp(incidentNumber, caseNumber, selectedReport);
    };
  }, [incidentNumber, caseNumber, selectedReport, onCleanUp]);

  return (
    <div className={`${styles.incidentNumberView} ${styles.withFooterButtons}`}>
      <SFScrollable
        className={styles.scrollable}
        containerClassName={styles.scrollableContainer}
      >
        <div className={styles.content}>
          <h2 className={styles.title}>Add Incident Numbers</h2>
          <p className={styles.subTitle}>
            You can add the CAD number if needed.
          </p>

          <div className={styles.form}>
            <SFTextField
              label="Incident Number"
              required
              disabled={incidentNumberDisabled}
              value={incidentNumber}
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              onChange={(event): void => setIncidentNumber(event.target.value)}
              helperText={INCIDENT_NUMBER_HELP_MSG}
              error={!isIncidentNumberValid(incidentNumber)}
            />

            <SFDivider />

            {caseNumberConfig && (
              <div className={styles.caseNumber}>
                <div className={styles.caseNumberTitle}>
                  <SFText type="component-1-medium">ID to Link Reports</SFText>
                  <SFChip label="OPTIONAL" size="small" sfColor="default" />
                </div>
                <CaseNumberField
                  label={caseNumberConfig.label}
                  required={caseNumberConfig.required}
                  value={
                    selectedReport
                      ? (selectedReport.general_information
                          .incident_number as string)
                      : caseNumber
                  }
                  options={caseNumberOptions()}
                  onChange={onCaseNumberChange}
                  helperText={caseNumberConfig.hint}
                  error={
                    caseNumber.length > 0 && !isIncidentNumberValid(caseNumber)
                  }
                />
              </div>
            )}
          </div>
        </div>
      </SFScrollable>

      <footer>
        <FooterButtons onCancel={onCancel} buttonAction={continueButton} />
      </footer>
    </div>
  );
};

export default IncidentNumberView;
